/**
 * admin/src/pages/StudentsPage.tsx
 * Danh sách học sinh toàn nền tảng
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStudents } from '../hooks/useStudents';
import {
  DataTable,
  TierBadge,
  Pagination,
  SearchInput,
  ErrorBanner,
  timeAgo,
  toast,
} from '../components/ui/index';
import type { Column } from '../components/ui/index';
import type { AdminStudentProfile, SubscriptionTier } from '../types/admin';

// ─── Sort options ─────────────────────────────────────────────────────────────

type SortKey = 'total_stars' | 'current_streak' | 'total_sessions';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'total_stars',     label: '⭐ Sao' },
  { key: 'current_streak',  label: '🔥 Streak' },
  { key: 'total_sessions',  label: '📝 Sessions' },
];

// ─── Tier filter options ──────────────────────────────────────────────────────

const TIER_OPTIONS: { value: SubscriptionTier | ''; label: string }[] = [
  { value: '',        label: 'Tất cả tier PH' },
  { value: 'member',   label: '👤 Member' },
  { value: 'premium',  label: '💜 Premium' },
];

// ─── Avatar emoji map ─────────────────────────────────────────────────────────

const AVATAR_EMOJIS: Record<string, string> = {
  cat: '🐱', dog: '🐶', fox: '🦊', rabbit: '🐰',
  bear: '🐻', panda: '🐼', owl: '🦉', penguin: '🐧',
  lion: '🦁', tiger: '🐯', dragon: '🐲', unicorn: '🦄',
};

function avatarEmoji(avatarId: string): string {
  return AVATAR_EMOJIS[avatarId] ?? '🧒';
}

// ─── CSV Export ────────────────────────────────────────────────────

function exportStudentsCSV(students: AdminStudentProfile[]) {
  const headers = ['Tên học sinh', 'Email PH', 'Tier PH', 'Sao', 'Streak', 'Sessions'];
  const rows = students.map(s => [
    `"${s.display_name}"`,
    `"${s.parent_email}"`,
    s.parent_tier,
    s.total_stars,
    s.current_streak,
    s.total_sessions,
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `students-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast(`Đã xuất ${students.length} học sinh`, 'success');
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function StudentsPage() {
  // ── Filter / sort / pagination state ──────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | ''>('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('total_stars');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page on filter/sort change
  useEffect(() => {
    setPage(1);
  }, [tierFilter, sortKey, sortDir]);

  // ── Data ───────────────────────────────────────────────────────────────────
  const {
    students: studentsData,
    total,
    totalPages,
    isLoading,
    error,
    refetch,
  } = useStudents({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    tier: tierFilter || undefined,
    sort: sortKey,
    dir: sortDir,
  });

  // ── Sort toggle ────────────────────────────────────────────────────────────
  function handleSortChip(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  // ── Table columns ──────────────────────────────────────────────────────────
  const studentColumns: Column<AdminStudentProfile>[] = [
    {
      key: 'display_name',
      label: 'Học sinh',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>{avatarEmoji(row.avatar_id)}</span>
          <div>
            <div className="cell-primary">{row.display_name}</div>
            <div className="cell-muted">PH: {row.parent_email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'total_stars',
      label: '⭐ Sao',
      width: '90px',
      render: (row) => (
        <span style={{ fontWeight: 600 }}>{row.total_stars.toLocaleString('vi-VN')}</span>
      ),
    },
    {
      key: 'current_streak',
      label: '🔥 Streak',
      width: '100px',
      render: (row) => (
        <span>
          {row.current_streak > 0 ? (
            <>{row.current_streak} ngày</>
          ) : (
            <span className="cell-muted">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'total_sessions',
      label: '📝 Sessions',
      width: '100px',
      render: (row) => row.total_sessions.toLocaleString('vi-VN'),
    },
    {
      key: 'parent_tier',
      label: 'Tier PH',
      width: '90px',
      render: (row) => <TierBadge tier={row.parent_tier} />,
    },
    {
      key: 'last_active',
      label: 'Hoạt động',
      width: '130px',
      render: (row) =>
        row.last_active ? (
          <span className="cell-muted">{timeAgo(row.last_active)}</span>
        ) : (
          <span className="cell-muted">Chưa hoạt động</span>
        ),
    },
    {
      key: 'actions',
      label: '',
      width: '90px',
      render: (row) => (
        <Link
          to={`/admin/students/${row.id}`}
          className="btn btn-ghost btn-sm"
          onClick={(e) => e.stopPropagation()}
        >
          👁 Xem
        </Link>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Học sinh</h1>
          <p>Danh sách học sinh toàn nền tảng · {total.toLocaleString('vi-VN')} học sinh</p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => exportStudentsCSV(studentsData)}
          disabled={studentsData.length === 0}
          title="Xuất trang hiện tại thành CSV"
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner message={error} onRetry={refetch} />
        </div>
      )}

      {/* Table card */}
      <div className="card">
        {/* Toolbar: search + tier filter */}
        <div className="table-toolbar">
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Tìm tên học sinh, email PH..."
          />
          <select
            className="input"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as SubscriptionTier | '')}
            style={{ minWidth: 160 }}
          >
            {TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="cell-muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
            {total.toLocaleString('vi-VN')} học sinh
          </span>
        </div>

        {/* Sort chips */}
        <div className="filter-chips" style={{ marginBottom: 12 }}>
          <span className="cell-muted" style={{ fontSize: 12, marginRight: 4 }}>Sắp xếp:</span>
          {SORT_OPTIONS.map(({ key, label }) => {
            const isActive = sortKey === key;
            return (
              <button
                key={key}
                className={`filter-chip ${isActive ? 'active' : ''}`}
                onClick={() => handleSortChip(key)}
              >
                {label}
                {isActive && (
                  <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Data table */}
        <div className="table-wrapper">
          <DataTable<Record<string, unknown>>
            columns={studentColumns as unknown as Column<Record<string, unknown>>[]}
            rows={studentsData as unknown as Record<string, unknown>[]}
            isLoading={isLoading}
            emptyText="Không tìm thấy học sinh nào"
          />
        </div>

        {/* Pagination */}
        {!isLoading && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalRows={total}
            pageSize={20}
            onPageChange={setPage}
            hasPrev={page > 1}
            hasNext={page < totalPages}
          />
        )}
      </div>
    </div>
  );
}
